import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CurrencyEur, CheckCircle, Clock } from "@phosphor-icons/react";
import CompensiGuide from "@/components/CompensiGuide";

const MONTH_LABELS = {
  "01": "Gennaio", "02": "Febbraio", "03": "Marzo", "04": "Aprile",
  "05": "Maggio", "06": "Giugno", "07": "Luglio", "08": "Agosto",
  "09": "Settembre", "10": "Ottobre", "11": "Novembre", "12": "Dicembre",
};

function fmtMonth(m) {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  return `${MONTH_LABELS[mm] || mm} ${y}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso.slice(0, 10); }
}

export default function MyCompensations() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("__all__");

  useEffect(() => {
    api.get(`/compensations/operator/${user.id}`).then(r => setData(r.data));
  }, [user.id]);

  if (!data) return <div className="p-8 text-muted-foreground">Caricamento...</div>;

  const allMonths = data.months.map(m => m.month);
  const shown = selectedMonth === "__all__" ? data.months : data.months.filter(m => m.month === selectedMonth);

  return (
    <div>
      <div className="mb-8 flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Compensi</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">I miei compensi</h1>
          <p className="text-muted-foreground mt-2">Storico dei servizi svolti e pagamenti ricevuti</p>
        </div>
        <div className="flex items-center gap-3">
          <CompensiGuide mode="button" />
          <CompensiGuide mode="tooltip" />
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Mese</label>
          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
            className="h-9 border rounded px-3 text-sm bg-background" data-testid="month-selector-my">
            <option value="__all__">Tutti i mesi</option>
            {allMonths.map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="kpi-card">
          <div className="label-eyebrow">Totale pagato</div>
          <div className="font-display text-3xl font-bold text-green-700 mt-2" data-testid="total-paid">€{data.total_paid.toFixed(2)}</div>
        </div>
        <div className="kpi-card">
          <div className="label-eyebrow">Da ricevere</div>
          <div className="font-display text-3xl font-bold text-amber-700 mt-2" data-testid="total-unpaid">€{data.total_unpaid.toFixed(2)}</div>
        </div>
        <div className="kpi-card">
          <div className="label-eyebrow">Servizi svolti</div>
          <div className="font-display text-3xl font-bold mt-2">{data.months.reduce((a, m) => a + m.count, 0)}</div>
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="kpi-card text-center py-16 text-muted-foreground">Nessun compenso registrato nel periodo selezionato.</div>
      ) : shown.map(m => (
        <div key={m.month} className="mb-6" data-testid={`month-${m.month}`}>
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="font-display text-xl font-semibold">{fmtMonth(m.month)}</h2>
            <div className="text-sm text-muted-foreground">
              {m.count} servizi · <span className="text-green-700 font-semibold">€{m.total_paid.toFixed(2)}</span> pagati · <span className="text-amber-700 font-semibold">€{m.total_unpaid.toFixed(2)}</span> in attesa
            </div>
          </div>
          <div className="kpi-card p-0 overflow-hidden">
            <table className="w-full text-sm mobile-cards">
              <thead className="bg-[color:hsl(var(--surface-alt))]"><tr className="text-left">
                <th className="py-2 px-4">Data</th>
                <th>Servizio</th>
                <th>Indirizzo</th>
                <th>Professionista</th>
                <th className="text-right">Compenso</th>
                <th className="text-right pr-4">Stato</th>
              </tr></thead>
              <tbody>
                {m.items.map(c => {
                  const addr = [c.property_address, c.property_city].filter(Boolean).join(", ") || "—";
                  return (
                  <tr key={c._id} className="border-t" data-testid={`comp-${c._id}`}>
                    <td data-label="Data" className="py-2 px-4">{fmtDate(c.completed_at)}</td>
                    <td data-label="Servizio" className="font-medium">{c.practice_service_name}</td>
                    <td data-label="Indirizzo" className="text-muted-foreground" data-testid={`comp-address-${c._id}`}>{addr}</td>
                    <td data-label="Professionista" className="text-muted-foreground" data-testid={`comp-professional-${c._id}`}>{c.professional_name || "—"}</td>
                    <td data-label="Compenso" className="text-right font-mono">€{(c.amount || 0).toFixed(2)}</td>
                    <td data-label="Stato" className="text-right pr-4">
                      {c.paid ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
                          <CheckCircle size={14} weight="fill" /> Pagato
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
                          <Clock size={14} weight="fill" /> In attesa
                        </span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
